import { create } from 'zustand';

export type RetrySessionStatus = 'Retrying' | 'Pending' | 'Success' | 'Failed';

type RetryPaymentState = {
  statuses: Record<string, RetrySessionStatus>;
  // Derived from `statuses` and kept in sync inside the setters so the
  // reference stays stable while the set of retrying ids is unchanged —
  // consumers can subscribe with a plain selector and won't re-render on
  // unrelated status mutations.
  retryingSessions: string[];
  // Incremented on every transition into 'Success'. Lets consumers run an
  // effect on every successful retry (not just the first one) by using this
  // counter as the dep.
  successfulPaymentCount: number;
  chargingStatuses: Record<string, RetrySessionStatus>;
  retryingChargingSessions: string[];
};

type RetryPaymentActions = {
  startPolling: (sessionId: string) => void;
  setSessionStatus: (sessionId: string, status: RetrySessionStatus) => void;
  getSessionStatus: <T>(
    sessionId: string,
    sessionPaymentState: T
  ) => RetrySessionStatus | T;
  getRetryingSessions: () => string[];
  startChargingPolling: (sessionId: string) => void;
  setChargingSessionStatus: (
    sessionId: string,
    status: RetrySessionStatus
  ) => void;
  getChargingSessionStatus: <T>(
    sessionId: string,
    sessionPaymentState: T
  ) => RetrySessionStatus | T;
  getRetryingChargingSessions: () => string[];
};

function checkSuccessfulPaymentCount(
  newStatus: RetrySessionStatus,
  sessionId: string,
  statusMap: Record<string, RetrySessionStatus>,
  currentCount: number
) {
  const isNowSuccess = newStatus === 'Success';
  const wasSuccess = statusMap[sessionId] === 'Success';
  return isNowSuccess && !wasSuccess ? currentCount + 1 : currentCount;
}

export const useRetryPaymentStore = create<
  RetryPaymentState & RetryPaymentActions
>((set, get) => ({
  statuses: {},
  retryingSessions: [],
  successfulPaymentCount: 0,
  chargingStatuses: {},
  retryingChargingSessions: [],
  startPolling: (sessionId) =>
    set((state) => ({
      statuses: { ...state.statuses, [sessionId]: 'Retrying' },
      retryingSessions: state.retryingSessions.includes(sessionId)
        ? state.retryingSessions
        : [...state.retryingSessions, sessionId],
    })),
  setSessionStatus: (sessionId, newStatus) =>
    set((state) => {
      const wasRetrying = state.retryingSessions.includes(sessionId);
      const isNowRetrying = newStatus === 'Retrying';
      let retryingSessions = state.retryingSessions;
      if (isNowRetrying && !wasRetrying) {
        retryingSessions = [...state.retryingSessions, sessionId];
      } else if (!isNowRetrying && wasRetrying) {
        retryingSessions = state.retryingSessions.filter(
          (id) => id !== sessionId
        );
      }
      // Count every transition into Success so consumers refetch even on
      // subsequent successes (the counter changes; a boolean would not).
      const successfulPaymentCount = checkSuccessfulPaymentCount(
        newStatus,
        sessionId,
        state.statuses,
        state.successfulPaymentCount
      );
      return {
        statuses: { ...state.statuses, [sessionId]: newStatus },
        retryingSessions,
        successfulPaymentCount,
      };
    }),
  // Once the user has clicked Retry, the retry-payment store is the source of
  // truth. Before that, the store is empty and we must fall back to the
  // session's own paymentState
  getSessionStatus: (sessionId, sessionPaymentState) =>
    get().statuses[sessionId] ?? sessionPaymentState,
  getRetryingSessions: () => get().retryingSessions,
  startChargingPolling: (sessionId) =>
    set((state) => {
      const updated = state.retryingChargingSessions.includes(sessionId)
        ? state.retryingChargingSessions
        : [...state.retryingChargingSessions, sessionId];

      return {
        chargingStatuses: {
          ...state.chargingStatuses,
          [sessionId]: 'Retrying',
        },
        retryingChargingSessions: updated,
      };
    }),
  setChargingSessionStatus: (sessionId, newStatus) =>
    set((state) => {
      const wasRetrying = state.retryingChargingSessions.includes(sessionId);
      const isNowRetrying = newStatus === 'Retrying';
      let retryingChargingSessions = state.retryingChargingSessions;
      if (isNowRetrying && !wasRetrying) {
        retryingChargingSessions = [
          ...state.retryingChargingSessions,
          sessionId,
        ];
      } else if (!isNowRetrying && wasRetrying) {
        retryingChargingSessions = state.retryingChargingSessions.filter(
          (id) => id !== sessionId
        );
      }
      const successfulPaymentCount = checkSuccessfulPaymentCount(
        newStatus,
        sessionId,
        state.chargingStatuses,
        state.successfulPaymentCount
      );
      return {
        chargingStatuses: {
          ...state.chargingStatuses,
          [sessionId]: newStatus,
        },
        retryingChargingSessions,
        successfulPaymentCount,
      };
    }),
  getChargingSessionStatus: (sessionId, sessionPaymentState) =>
    get().chargingStatuses[sessionId] ?? sessionPaymentState,
  getRetryingChargingSessions: () => get().retryingChargingSessions,
}));
